import type { Dict } from '../index.js';
import { expandAll } from './server.js';

/**
 * Arabic: Egyptian leave rules, medical reports, absences paid from the balance,
 * projects and the internal policy page. Keys are the exact English text.
 */

// ---- Leave rule errors from the server (also shown as "Cannot approve: …") ----
const ruleErrors: [string, string][] = [
  ['"{type}" can be taken after {n} months of service (from {date}).', 'يمكن أخذ "{type}" بعد {n} شهرًا من الخدمة (اعتبارًا من {date}).'],
  ['"{type}" allows at most [[max]] in a row.', 'يسمح "{type}" بحد أقصى {max} يوم متتالي.'],
  [
    'There must be at least {gap} days between two "{type}" requests (another one is on {date}).',
    'يجب أن يفصل {gap} يومًا على الأقل بين طلبين من "{type}" (يوجد طلب آخر في {date}).',
  ],
  [
    'There must be at least {gap} days between two "{type}" requests (another one is from {start} to {end}).',
    'يجب أن يفصل {gap} يومًا على الأقل بين طلبين من "{type}" (يوجد طلب آخر من {start} إلى {end}).',
  ],
  ['"{type}" can be taken only once during the employment.', 'يمكن أخذ "{type}" مرة واحدة فقط طوال فترة العمل.'],
  ['"{type}" can be taken at most {n} times during the employment.', 'يمكن أخذ "{type}" بحد أقصى {n} مرات طوال فترة العمل.'],
];

const server: Dict = {
  ...expandAll(ruleErrors),
  ...expandAll(ruleErrors.map(([en, ar]) => [`Cannot approve: ${en}`, `تعذّرت الموافقة: ${ar}`])),
  'Days are taken from your {type} balance.': 'تُخصم الأيام من رصيد {type}.',
  'A medical report must be attached (PDF or photo).': 'يجب إرفاق تقرير طبي (ملف PDF أو صورة).',
  '"{type}" needs a medical report. Please attach it (PDF or photo).': 'يتطلب "{type}" تقريرًا طبيًا. يرجى إرفاقه (ملف PDF أو صورة).',
  'The attached document is not valid for this request. Please upload it again.': 'المستند المرفق غير صالح لهذا الطلب. يرجى رفعه مرة أخرى.',
  'A leave type cannot take days from itself': 'لا يمكن لنوع إجازة أن يخصم الأيام من نفسه',
  'That leave type itself takes days from another type': 'نوع الإجازة هذا يخصم أيامه من نوع آخر',
  'Unknown leave type': 'نوع إجازة غير معروف',
  'Choose another type': 'اختر نوعًا آخر',
  'Choose a type with its own balance': 'اختر نوعًا له رصيد خاص به',
  // Files
  'The file is empty': 'الملف فارغ',
  'The file is larger than 5 MB. Take a smaller photo or scan.': 'حجم الملف أكبر من 5 ميجابايت. التقط صورة أو نسخة ممسوحة أصغر.',
  'Only PDF files and JPG or PNG photos can be attached.': 'يمكن إرفاق ملفات PDF وصور JPG أو PNG فقط.',
  'A medical report belongs to an employee.': 'التقرير الطبي يخص موظفًا محددًا.',
  'You can only attach your own medical report.': 'يمكنك إرفاق تقريرك الطبي فقط.',
  'Only administrators can upload the policy document.': 'يمكن لمسؤولي النظام فقط رفع مستند اللائحة.',
  'Upload the policy document again': 'ارفع مستند اللائحة مرة أخرى',
  // Projects
  'A project with this name or code already exists.': 'يوجد مشروع بهذا الاسم أو الرمز بالفعل.',
  'This person is already on the project for those dates.': 'هذا الشخص موجود في المشروع بالفعل في هذه التواريخ.',
  'This project has (or had) people on it and is kept for history. Mark it as completed instead.':
    'هذا المشروع به (أو كان به) أشخاص ويُحتفظ به للسجل. اجعل حالته «مكتمل» بدلًا من ذلك.',
  'The end date is before the date they joined': 'تاريخ الانتهاء قبل تاريخ الانضمام',
  // Payroll notes
  '{n} absent day(s) paid from the leave balance ({list}) — recorded as leave when the reporting period is closed.':
    '{n} يوم غياب مدفوع من رصيد الإجازات ({list}) — يُسجَّل كإجازة عند إقفال فترة التقرير.',
  '{n} absent day(s) not covered by leave — counted as 0 hours ({dates}).': '{n} يوم غياب غير مغطّى بإجازة — يُحتسب 0 ساعة ({dates}).',
  // Notifications and automatic records
  'New leave year: {days} days of {type}': 'سنة إجازات جديدة: {days} يوم من {type}',
  'Your {type} balance for the new leave year is ready.': 'رصيد {type} لسنة الإجازات الجديدة جاهز.',
  '1 absent day taken from your leave balance': 'خُصم يوم غياب واحد من رصيد إجازاتك',
  '{n} absent days taken from your leave balance': 'خُصم {n} أيام غياب من رصيد إجازاتك',
  'The internal policy was updated': 'تم تحديث اللائحة الداخلية',
  'Absent without approved leave — paid from the balance': 'غياب دون إجازة معتمدة — مدفوع من الرصيد',
  'Recorded automatically when the reporting period was closed': 'سُجِّل تلقائيًا عند إقفال فترة التقرير',
  'Absent day paid from the balance': 'يوم غياب مدفوع من الرصيد',
  'Egyptian labour law preset: leave years follow each start date': 'إعداد قانون العمل المصري: سنة الإجازات تبدأ من تاريخ تعيين كل موظف',
};

// ---- Leave types of the Egyptian preset ----
const leaveTypes: Dict = {
  'Sick Leave (without medical report)': 'إجازة مرضية (بدون تقرير طبي)',
  'Sick Leave (with medical report)': 'إجازة مرضية (بتقرير طبي)',
  'Marriage Leave': 'إجازة زواج',
  'Bereavement Leave': 'إجازة وفاة',
  'Hajj Leave': 'إجازة حج',
  'Egyptian labour law as applied by PCBS.': 'قانون العمل المصري كما تطبّقه PCBS.',
  '15 working days in the first year of service, 21 from the second year, 30 after 10 years of experience or from age 50, 45 with a disability. Not available in the first 3 months. Weekends and public holidays are not counted.':
    '15 يوم عمل في السنة الأولى من الخدمة، و21 يومًا من السنة الثانية، و30 يومًا بعد 10 سنوات خبرة أو من سن 50، و45 يومًا لذوي الإعاقة. غير متاحة في أول 3 أشهر. لا تُحتسب عطلات نهاية الأسبوع والعطلات الرسمية.',
  'For urgent events that could not be announced in advance. Up to 7 days a year, at most 2 days in a row, taken from the annual vacation balance.':
    'للظروف الطارئة التي لا يمكن الإبلاغ عنها مسبقًا. حتى 7 أيام في السنة، وبحد أقصى يومين متتاليين، وتُخصم من رصيد الإجازة السنوية.',
  '5 days a year without a medical report. At most 2 days in a row, and at least one week between two sick leaves.':
    '5 أيام في السنة بدون تقرير طبي. بحد أقصى يومين متتاليين، وأسبوع على الأقل بين إجازتين مرضيتين.',
  'A medical report must be uploaded with the request. Reviewed and approved by an administrator.': 'يجب رفع تقرير طبي مع الطلب. يراجعه ويعتمده مسؤول النظام.',
  'By agreement with the company (training, studies, personal reasons). Not paid.': 'بالاتفاق مع الشركة (تدريب، دراسة، أسباب شخصية). غير مدفوعة.',
  'Company policy: 3 paid days. Does not use the annual vacation balance.': 'سياسة الشركة: 3 أيام مدفوعة. لا تُخصم من رصيد الإجازة السنوية.',
  '120 calendar days (4 months), fully paid. Up to 3 times during the employment.': '120 يومًا تقويميًا (4 أشهر) مدفوعة بالكامل. حتى 3 مرات طوال فترة العمل.',
  '1 paid day, on the day the child is born. Up to 3 times during the employment.': 'يوم واحد مدفوع في يوم ولادة الطفل. حتى 3 مرات طوال فترة العمل.',
  'Company policy: 3 paid days for the death of an immediate family member.': 'سياسة الشركة: 3 أيام مدفوعة عند وفاة أحد أفراد الأسرة المباشرين.',
  '15 fully paid days, once during the employment, after 5 years of service.': '15 يومًا مدفوعة بالكامل، مرة واحدة طوال فترة العمل، بعد 5 سنوات من الخدمة.',
};

// ---- Settings ----
const settings: Dict = {
  'Annual leave in the first year of service (days)': 'الإجازة السنوية في السنة الأولى من الخدمة (أيام)',
  'Used by leave policies set to "Egyptian labour law". Egyptian law: 15 working days.': 'تُستخدم في سياسات الإجازات المضبوطة على «قانون العمل المصري». القانون المصري: 15 يوم عمل.',
  'Annual leave from the second year of service (days)': 'الإجازة السنوية من السنة الثانية من الخدمة (أيام)',
  'Egyptian law: 21 working days.': 'القانون المصري: 21 يوم عمل.',
  'Annual leave after 10 years of experience or from age 50 (days)': 'الإجازة السنوية بعد 10 سنوات خبرة أو من سن 50 (أيام)',
  'Experience counts years with any employer (entered on the employee). Egyptian law: 30 working days.':
    'تُحتسب الخبرة لدى أي صاحب عمل (تُدخل في بيانات الموظف). القانون المصري: 30 يوم عمل.',
  'Annual leave for employees with a disability (days)': 'الإجازة السنوية للموظفين ذوي الإعاقة (أيام)',
  'Egyptian law: 45 working days.': 'القانون المصري: 45 يوم عمل.',
  'Absent days without approved leave': 'أيام الغياب دون إجازة معتمدة',
  'annual_then_sick = paid from the annual leave balance, then from sick leave without a report; only when both are used up is the day deducted from the salary. annual = annual leave balance only. none = always deducted. Recorded as leave when the reporting period is closed.':
    'من السنوية ثم المرضية = يُدفع اليوم من رصيد الإجازة السنوية، ثم من الإجازة المرضية بدون تقرير؛ ولا يُخصم من الراتب إلا بعد نفاد الرصيدين. السنوية فقط = من رصيد الإجازة السنوية فقط. بدون = يُخصم دائمًا. يُسجَّل كإجازة عند إقفال فترة التقرير.',
  'From annual leave, then sick leave without a report, then unpaid': 'من الإجازة السنوية، ثم المرضية بدون تقرير، ثم بدون أجر',
  'From annual leave, then unpaid': 'من الإجازة السنوية، ثم بدون أجر',
  'Always unpaid': 'دائمًا بدون أجر',
};

// ---- Screens ----
const web: Dict = {
  // Balances
  '{used} of {max} days used this year': 'استُخدم {used} من {max} يوم هذه السنة',
  'used {n} of {max} times': 'استُخدمت {n} من {max} مرات',
  'Already used': 'استُخدمت',
  'Not used yet': 'لم تُستخدم بعد',
  'Can be used from {date}': 'متاحة اعتبارًا من {date}',
  // Files
  'Attach file': 'إرفاق ملف',
  'PDF, JPG or PNG, up to 5 MB.': 'PDF أو JPG أو PNG، حتى 5 ميجابايت.',
  'Medical report': 'تقرير طبي',
  'Attach medical report': 'إرفاق التقرير الطبي',
  'Absent day — taken from the balance': 'يوم غياب — خُصم من الرصيد',
  // Rules
  'Medical report required': 'يتطلب تقريرًا طبيًا',
  'Up to {days} a year': 'حتى {days} في السنة',
  'At most {days} in a row': 'بحد أقصى {days} متتالية',
  'At least {n} days between two requests': '{n} يومًا على الأقل بين طلبين',
  'After {n} months of service': 'بعد {n} شهرًا من الخدمة',
  'Once during the employment': 'مرة واحدة طوال فترة العمل',
  'Up to {n} times during the employment': 'حتى {n} مرات طوال فترة العمل',
  'Calendar days (weekends included)': 'أيام تقويمية (تشمل عطلات نهاية الأسبوع)',
  'Taken from the {name} balance': 'تُخصم من رصيد {name}',
  // Employee
  'Date of birth': 'تاريخ الميلاد',
  'Years of experience before joining': 'سنوات الخبرة قبل الانضمام',
  'Has a disability': 'من ذوي الإعاقة',
  'Projects ({n})': 'المشاريع ({n})',
  'Not on any project': 'غير مُسند إلى أي مشروع',
  'For the annual leave rules': 'لقواعد الإجازة السنوية',
  'Egyptian law: 21 days from the second year, 30 days after 10 years of experience or from age 50, 45 days with a disability. Visible to administrators only.':
    'القانون المصري: 21 يومًا من السنة الثانية، و30 يومًا بعد 10 سنوات خبرة أو من سن 50، و45 يومًا لذوي الإعاقة. تظهر لمسؤولي النظام فقط.',
  'With any employer': 'لدى أي صاحب عمل',
  // Leave setup
  'Egyptian labour law applied: {created} new and {updated} updated leave types, policy "{policy}".':
    'تم تطبيق قانون العمل المصري: {created} نوع إجازة جديد و{updated} نوع محدَّث، وسياسة "{policy}".',
  'Apply Egyptian labour law': 'تطبيق قانون العمل المصري',
  'Most days in a row': 'أقصى عدد أيام متتالية',
  'Empty = no limit (e.g. 2 for sick leave without a report)': 'فارغ = بلا حد (مثل 2 للإجازة المرضية بدون تقرير)',
  'Days needed between two requests': 'الأيام المطلوبة بين طلبين',
  'Empty = none (e.g. 7 = one week)': 'فارغ = لا يوجد (مثل 7 = أسبوع)',
  'Available after (months of service)': 'متاحة بعد (أشهر الخدمة)',
  'Empty = from the first day (e.g. 3 for annual leave)': 'فارغ = من اليوم الأول (مثل 3 للإجازة السنوية)',
  'Times allowed during the employment': 'عدد المرات المسموح بها طوال فترة العمل',
  'Empty = no limit (e.g. 1 for Hajj)': 'فارغ = بلا حد (مثل 1 للحج)',
  'Take the days from another balance': 'خصم الأيام من رصيد آخر',
  'e.g. emergency leave comes out of annual vacation': 'مثل: الإجازة الطارئة تُخصم من الإجازة السنوية',
  'No — its own balance': 'لا — له رصيد خاص',
  'A PDF or photo must be attached to every request.': 'يجب إرفاق ملف PDF أو صورة مع كل طلب.',
  'Count calendar days': 'احتساب الأيام التقويمية',
  'Weekends and holidays count too (e.g. maternity leave).': 'تُحتسب عطلات نهاية الأسبوع والعطلات أيضًا (مثل إجازة الأمومة).',
  'Apply Egyptian labour law?': 'تطبيق قانون العمل المصري؟',
  'Creates or updates these leave types with their rules: annual vacation (15 / 21 / 30 / 45 days, not in the first 3 months), emergency (7 days, from annual), sick leave without a report (5 days, at most 2 in a row, a week apart), sick leave with a medical report, unpaid, marriage, maternity, paternity, bereavement and Hajj. The "Egypt — standard" policy is set to the law and each person\'s leave year starts on their start date. Existing balances keep their figures.':
    'يُنشئ أو يحدّث أنواع الإجازات التالية بقواعدها: الإجازة السنوية (15 / 21 / 30 / 45 يومًا، وغير متاحة في أول 3 أشهر)، والطارئة (7 أيام من السنوية)، والمرضية بدون تقرير (5 أيام، بحد أقصى يومين متتاليين وبفاصل أسبوع)، والمرضية بتقرير طبي، وغير المدفوعة، والزواج، والأمومة، والأبوة، والوفاة، والحج. تُضبط سياسة "مصر — قياسية" وفق القانون، وتبدأ سنة الإجازات لكل موظف من تاريخ تعيينه. تبقى الأرصدة الحالية كما هي.',
  Apply: 'تطبيق',
  '15 / 21 / 30 / 45 days (Egyptian law)': '15 / 21 / 30 / 45 يومًا (القانون المصري)',
  'Fixed number': 'عدد ثابت',
  'Egyptian law': 'القانون المصري',
  // Policy
  'Internal Policy': 'اللائحة الداخلية',
  'Version {n} · updated {date} by {name}': 'الإصدار {n} · حُدِّث في {date} بواسطة {name}',
  'The company rules everyone works by.': 'قواعد الشركة التي يعمل بها الجميع.',
  'Earlier versions': 'الإصدارات السابقة',
  'PCBS internal policy': 'اللائحة الداخلية لشركة PCBS',
  'Edit policy': 'تعديل اللائحة',
  'Write the policy': 'كتابة اللائحة',
  'The internal policy has not been published yet': 'لم تُنشر اللائحة الداخلية بعد',
  'Write it here or attach the policy PDF. Everyone will be able to read it.': 'اكتبها هنا أو أرفق ملف PDF للائحة. سيتمكن الجميع من قراءتها.',
  'It will appear here once the administration publishes it.': 'ستظهر هنا بمجرد أن تنشرها الإدارة.',
  Publish: 'نشر',
  Title: 'العنوان',
  'Policy text': 'نص اللائحة',
  'Leave an empty line between paragraphs. Start a line with "# " for a heading and "- " for a bullet point.':
    'اترك سطرًا فارغًا بين الفقرات. ابدأ السطر بـ "# " للعنوان و"- " للنقطة.',
  'Policy document (optional)': 'مستند اللائحة (اختياري)',
  'Attach PDF': 'إرفاق PDF',
  'Notify everyone': 'إشعار الجميع',
  'Sends a notification that the policy was updated.': 'يرسل إشعارًا بتحديث اللائحة.',
  'Every published version is kept.': 'يُحتفظ بكل إصدار منشور.',
  'Version {n}': 'الإصدار {n}',
  '{date} by {name}': '{date} بواسطة {name}',
  'Show text': 'عرض النص',
  // Projects
  Projects: 'المشاريع',
  'Edit project': 'تعديل المشروع',
  'New project': 'مشروع جديد',
  'Project name': 'اسم المشروع',
  'e.g. Riyadh warehouse': 'مثل: مستودع الرياض',
  Optional: 'اختياري',
  Client: 'العميل',
  Planned: 'مخطط',
  'On hold': 'متوقف مؤقتًا',
  Completed: 'مكتمل',
  'Which engineers work on which project. An engineer can be on several projects.': 'أي مهندس يعمل على أي مشروع. يمكن للمهندس أن يكون في عدة مشاريع.',
  'Current projects': 'المشاريع الحالية',
  'All projects, including completed': 'كل المشاريع، بما فيها المكتملة',
  'No projects yet': 'لا توجد مشاريع بعد',
  'Create a project, then add the engineers who work on it.': 'أنشئ مشروعًا ثم أضف المهندسين الذين يعملون عليه.',
  '1 person': 'شخص واحد',
  '{n} people': '{n} أشخاص',
  'Project not found': 'المشروع غير موجود',
  'On the project ({n})': 'في المشروع ({n})',
  'Add person': 'إضافة شخص',
  'Nobody on this project yet': 'لا يوجد أحد في هذا المشروع بعد',
  'since {date}': 'منذ {date}',
  'until {date}': 'حتى {date}',
  'Take off project': 'إخراج من المشروع',
  'Previously on the project': 'كانوا في المشروع سابقًا',
  'Add person to {name}': 'إضافة شخص إلى {name}',
  'Role on the project': 'الدور في المشروع',
  'Optional, e.g. Structural engineer': 'اختياري، مثل: مهندس إنشائي',
  'Take {name} off the project': 'إخراج {name} من المشروع',
  'Last day on the project': 'آخر يوم في المشروع',
  'The history is kept.': 'يُحتفظ بالسجل.',
  'Use this only to correct a mistake — to keep the history, use “Take off project” instead.': 'استخدم هذا لتصحيح خطأ فقط — للاحتفاظ بالسجل استخدم «إخراج من المشروع» بدلًا من ذلك.',
  'This project has never had anyone on it, so it can be deleted permanently.': 'لم يكن أحد في هذا المشروع من قبل، لذا يمكن حذفه نهائيًا.',
};

const excel: Dict = {
  'attlog.dat, .txt, .csv or the Excel report (.xls / .xlsx) — up to 10 MB. Employees are matched by their fingerprint machine ID (or employee ID).':
    'attlog.dat أو ‎.txt أو ‎.csv أو تقرير Excel ‏(‎.xls / .xlsx) — حتى 10 ميجابايت. تتم مطابقة الموظفين برقمهم في جهاز البصمة (أو الرقم الوظيفي).',
  "This is not an Excel file. Upload the .xls / .xlsx report, the machine's .dat log or a CSV file.":
    'هذا ليس ملف Excel. ارفع تقرير ‎.xls / .xlsx أو سجل الجهاز ‎.dat أو ملف CSV.',
  'This Excel file could not be read. Open it in Excel, save it as CSV and upload the CSV.': 'تعذّرت قراءة ملف Excel هذا. افتحه في Excel واحفظه بصيغة CSV ثم ارفع ملف CSV.',
  'Choose a file': 'اختر ملفًا',
};

export const phase9: Dict = {
  ...excel, ...server, ...leaveTypes, ...settings, ...web };
